import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  BarChart3,
  DollarSign,
  Video,
  Clock,
  Target,
  Play,
  Zap,
  FileText,
  CheckCircle2,
  Circle,
  Image,
  CreditCard,
  Megaphone,
  Upload,
  ChevronRight,
  AlertTriangle,
  X,
} from 'lucide-react';
import { ChartAreaInteractive } from '@/components/chart-area-interactive';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { api } from '../services/api';
import { Work, CrowdfundingCampaign } from '../types/slicetv';

interface ActivityItem {
  type: string;
  title: string;
  description: string;
  timestamp: string;
}

const formatViews = (views: number): string => {
  if (views >= 1_000_000) return `${(views / 1_000_000).toFixed(1)}M`;
  if (views >= 1_000) return `${(views / 1_000).toFixed(1)}K`;
  return views.toLocaleString();
};

const formatTimeAgo = (timestamp: string) => {
  const now = new Date();
  const date = new Date(timestamp);
  const diffMs = now.getTime() - date.getTime();
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffHours / 24);

  if (diffDays > 0) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  if (diffHours > 0)
    return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  return 'Just now';
};

const activityIcon = (type: string) => {
  switch (type) {
    case 'slice_donation':
      return { Icon: Zap, color: 'text-primary' };
    case 'campaign_update':
      return { Icon: Target, color: 'text-blue-500' };
    default:
      return { Icon: Clock, color: 'text-muted-foreground' };
  }
};

// ─── Setup Checklist ──────────────────────────────────────────────────────────

interface ChecklistState {
  businessInfo: boolean;
  contract: boolean;
  studioPage: boolean;
  stripe: boolean;
  firstCampaign: boolean;
  firstUpload: boolean;
}

interface ChecklistItem {
  key: keyof ChecklistState;
  title: string;
  description: string;
  icon: React.ElementType;
  action: string;
  route: string;
  requiresStripe?: boolean;
}

const CHECKLIST_ITEMS: ChecklistItem[] = [
  {
    key: 'studioPage',
    title: 'Complete your studio page',
    description: 'Add a logo, banner, and about section so viewers can discover you.',
    icon: Image,
    action: 'Set up studio page',
    route: '/profile',
  },
  {
    key: 'stripe',
    title: 'Connect Stripe',
    description: 'Set up payouts so you can receive slices and pay upload fees.',
    icon: CreditCard,
    action: 'Connect Stripe',
    route: '/billing',
  },
  {
    key: 'firstCampaign',
    title: 'Create your first campaign',
    description: 'Fund your next project with slice-based crowdfunding.',
    icon: Megaphone,
    action: 'Create campaign',
    route: '/content',
    requiresStripe: true,
  },
  {
    key: 'firstUpload',
    title: 'Upload your first title',
    description: 'Add a movie, show, or documentary to your library.',
    icon: Upload,
    action: 'Upload title',
    route: '/content/new',
    requiresStripe: true,
  },
];

// ─── Dashboard Component ──────────────────────────────────────────────────────

const Dashboard = () => {
  const { publisher, refreshPublisher } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [checklistDismissed, setChecklistDismissed] = useState(
    publisher?.setup_checklist_dismissed === true
  );
  const [checklist, setChecklist] = useState<ChecklistState>({
    businessInfo: false,
    contract: false,
    studioPage: false,
    stripe: false,
    firstCampaign: false,
    firstUpload: false,
  });
  const [stripeGateTarget, setStripeGateTarget] = useState<string | null>(null);
  const [metrics, setMetrics] = useState({
    totalEarnings: 0,
    slicesReceived: 0,
    monthlySlices: 0,
    activeWorks: 0,
    totalViews: 0,
    activeCampaigns: 0,
    completedCampaigns: 0,
    upcomingReleases: 0,
  });
  const [recentWorks, setRecentWorks] = useState<Work[]>([]);
  const [activeCampaigns, setActiveCampaigns] = useState<
    CrowdfundingCampaign[]
  >([]);
  const [recentActivity, setRecentActivity] = useState<ActivityItem[]>([]);
  const [chartData, setChartData] = useState<{ date: string; slices: number }[]>([]);

  useEffect(() => {
    if (publisher?.setup_checklist_dismissed === true) {
      setChecklistDismissed(true);
    }
  }, [publisher?.setup_checklist_dismissed]);

  useEffect(() => {
    if (publisher?.id) {
      fetchDashboardData();
      computeChecklist();
    }
  }, [publisher]);

  const computeChecklist = async () => {
    if (!publisher?.id) return;

    // Count works and campaigns from DB
    const [worksResult, campaignsResult] = await Promise.all([
      supabase
        .from('works')
        .select('id', { count: 'exact', head: true })
        .eq('publisher_id', publisher.id),
      supabase
        .from('crowdfunding_campaigns')
        .select('id', { count: 'exact', head: true })
        .eq('publisher_id', publisher.id),
    ]);

    setChecklist({
      businessInfo: !!publisher.application_completed,
      contract: publisher.contract_signed === true,
      studioPage:
        !!publisher.brand_logo_url && !!publisher.brand_description,
      stripe: publisher.stripe_connected === true,
      firstCampaign: (campaignsResult.count ?? 0) > 0,
      firstUpload: (worksResult.count ?? 0) > 0,
    });
  };

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const [dashboardMetrics, works, campaigns, monthlyEarnings, activity] =
        await Promise.all([
          api.publisher.getDashboardMetrics(publisher!.id),
          api.works.getByPublisher(publisher!.id),
          api.campaigns.getByPublisher(publisher!.id),
          api.publisher.getMonthlyEarnings(publisher!.id),
          api.publisher.getRecentActivity(publisher!.id),
        ]);

      const recentWorks = works.slice(0, 5);
      const activeCampaigns = campaigns
        .filter((c: any) => ['active', 'pending'].includes(c.status))
        .slice(0, 3);

      setMetrics({
        ...dashboardMetrics,
        upcomingReleases: works.filter(
          (w: Work) => w.release_date && new Date(w.release_date) > new Date()
        ).length,
      });

      setRecentWorks(recentWorks);
      setActiveCampaigns(activeCampaigns);
      setChartData(monthlyEarnings);
      setRecentActivity(activity);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const dismissChecklist = async () => {
    setChecklistDismissed(true);
    if (publisher?.id) {
      await supabase
        .from('publishers')
        .update({ setup_checklist_dismissed: true })
        .eq('id', publisher.id);
      await refreshPublisher();
    }
  };

  const handleChecklistAction = (item: ChecklistItem) => {
    if (item.requiresStripe && !checklist.stripe) {
      setStripeGateTarget(item.key);
      return;
    }
    setStripeGateTarget(null);
    navigate(item.route);
  };

  // Checklist progress calculation
  const checklistKeys: (keyof ChecklistState)[] = [
    'businessInfo',
    'contract',
    'studioPage',
    'stripe',
    'firstCampaign',
    'firstUpload',
  ];
  const completedCount = checklistKeys.filter((k) => checklist[k]).length;
  const progressPercent = Math.round((completedCount / checklistKeys.length) * 100);
  const allComplete = completedCount === checklistKeys.length;
  const showChecklist = !checklistDismissed && !allComplete;
  const isFirstLoad = completedCount <= 2; // contract + business info only

  if (loading) {
    return (
      <div className='space-y-12'>
        {/* Header Skeleton */}
        <div className='flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4'>
          <div className='space-y-2'>
            <Skeleton className='h-8 w-64' />
            <Skeleton className='h-4 w-96' />
          </div>
          <div className='flex gap-2'>
            <Skeleton className='h-6 w-24' />
            <Skeleton className='h-6 w-20' />
          </div>
        </div>

        {/* Metrics Grid Skeleton */}
        <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6'>
          {[1, 2, 3, 4].map((i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className='h-4 w-24 mb-2' />
                <Skeleton className='h-8 w-32' />
              </CardHeader>
              <CardFooter>
                <Skeleton className='h-4 w-full' />
              </CardFooter>
            </Card>
          ))}
        </div>

        {/* Content Skeleton */}
        <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
          <Card>
            <CardHeader>
              <Skeleton className='h-6 w-32' />
            </CardHeader>
            <CardContent>
              <div className='space-y-5'>
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} className='h-20 w-full' />
                ))}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <Skeleton className='h-6 w-32' />
            </CardHeader>
            <CardContent>
              <div className='space-y-5'>
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} className='h-20 w-full' />
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className='space-y-8'>
      {/* Header */}
      <div>
        <h1 className='text-2xl md:text-3xl font-bold tracking-tight'>
          {isFirstLoad
            ? `Welcome to Kinora, ${publisher?.company_name || 'Publisher'}!`
            : 'Publisher Dashboard'}
        </h1>
        <p className='text-muted-foreground mt-2 text-sm md:text-base'>
          {isFirstLoad
            ? "Here's how to get started."
            : `Welcome back, ${publisher?.company_name || 'Publisher'}! Here's your Kinora overview.`}
        </p>
      </div>

      {/* ── First-run Setup Checklist ──────────────────────────────────── */}
      {showChecklist && (
        <Card className='border-primary/20 bg-primary/5'>
          <CardHeader className='pb-3'>
            <div className='flex items-start justify-between gap-4'>
              <div className='flex-1'>
                <CardTitle className='text-base flex items-center gap-2'>
                  Your publisher profile is {progressPercent}% complete
                </CardTitle>
                <CardDescription className='mt-1 text-xs'>
                  {completedCount} of {checklistKeys.length} steps done
                </CardDescription>
              </div>
              <button
                onClick={dismissChecklist}
                className='text-muted-foreground hover:text-foreground transition-colors cursor-pointer mt-0.5'
                aria-label='Dismiss checklist'
              >
                <X className='h-4 w-4' />
              </button>
            </div>
            <Progress value={progressPercent} className='h-2 mt-2' />
          </CardHeader>
          <CardContent className='pt-0'>
            {/* Stripe gate prompt */}
            {stripeGateTarget && (
              <Alert className='mb-4 border-yellow-500/40 bg-yellow-500/5'>
                <AlertTriangle className='h-4 w-4 text-yellow-500' />
                <AlertDescription className='flex items-center justify-between gap-4'>
                  <span className='text-sm'>
                    {stripeGateTarget === 'firstUpload'
                      ? 'Connect Stripe to start uploading. Upload fees are collected via Stripe.'
                      : 'Connect Stripe to create campaigns. Campaign fees are collected via Stripe.'}
                  </span>
                  <Button
                    size='sm'
                    variant='outline'
                    className='shrink-0'
                    onClick={() => { setStripeGateTarget(null); navigate('/billing'); }}
                  >
                    Connect Stripe
                  </Button>
                </AlertDescription>
              </Alert>
            )}

            <div className='grid gap-3 sm:grid-cols-2'>
              {CHECKLIST_ITEMS.map((item) => {
                const done = checklist[item.key];
                const Icon = item.icon;
                const locked = item.requiresStripe && !checklist.stripe;

                return (
                  <div
                    key={item.key}
                    className={`flex items-start gap-3 rounded-lg border p-4 transition-all duration-150 ${
                      done
                        ? 'border-green-500/20 bg-green-500/5 opacity-70'
                        : locked
                        ? 'border-border bg-muted/10 opacity-60'
                        : 'border-border bg-card hover:border-primary/40 hover:bg-primary/5 cursor-pointer'
                    }`}
                    onClick={() => !done && handleChecklistAction(item)}
                    role={!done ? 'button' : undefined}
                    aria-disabled={locked}
                  >
                    <div
                      className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
                        done
                          ? 'bg-green-500/15 text-green-500'
                          : locked
                          ? 'bg-muted text-muted-foreground'
                          : 'bg-primary/10 text-primary'
                      }`}
                    >
                      {done ? (
                        <CheckCircle2 className='h-4 w-4' />
                      ) : (
                        <Icon className='h-4 w-4' />
                      )}
                    </div>
                    <div className='flex-1 min-w-0'>
                      <p
                        className={`text-sm font-medium ${done ? 'line-through text-muted-foreground' : ''}`}
                      >
                        {item.title}
                      </p>
                      <p className='text-xs text-muted-foreground mt-0.5 leading-snug'>
                        {item.description}
                      </p>
                      {locked && (
                        <p className='text-xs text-yellow-500/80 mt-1'>
                          Requires Stripe connection
                        </p>
                      )}
                    </div>
                    {!done && !locked && (
                      <ChevronRight className='h-4 w-4 text-muted-foreground shrink-0 mt-2' />
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* All-complete confirmation */}
      {allComplete && !checklistDismissed && (
        <Alert className='border-green-500/30 bg-green-500/5 pr-10'>
          <CheckCircle2 className='h-4 w-4 text-green-500' />
          <AlertDescription className='text-sm'>
            Setup complete! Your publisher profile is fully configured.
          </AlertDescription>
          <button
            type='button'
            onClick={dismissChecklist}
            className='absolute right-3 top-3 text-muted-foreground hover:text-foreground transition-colors cursor-pointer'
            aria-label='Dismiss setup complete message'
          >
            <X className='h-4 w-4' />
          </button>
        </Alert>
      )}

      <Separator />

      {/* Earnings Chart */}
      <Card>
        <CardHeader>
          <CardTitle>Earnings Overview</CardTitle>
          <CardDescription>Slice earnings over the last 6 months</CardDescription>
        </CardHeader>
        <CardContent>
          <ChartAreaInteractive data={chartData} />
        </CardContent>
      </Card>

      {/* Key Metrics Grid */}
      <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6'>
        <Card className="hover:shadow-[0_4px_16px_rgba(0,0,0,0.5)] transition-shadow">
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2 text-xs mb-2">
              <DollarSign className="h-4 w-4 text-muted-foreground" />
              Total Earnings
            </CardDescription>
            <CardTitle className="text-3xl font-bold tabular-nums">
              {metrics.totalEarnings > 0 ? `$${metrics.totalEarnings.toLocaleString()}` : '$0'}
            </CardTitle>
          </CardHeader>
          <CardFooter className="pt-0">
            <p className="text-xs text-muted-foreground">
              {metrics.monthlySlices > 0
                ? `${metrics.monthlySlices} slices this month`
                : `${metrics.slicesReceived} slices received`}
            </p>
          </CardFooter>
        </Card>

        <Card className="hover:shadow-[0_4px_16px_rgba(0,0,0,0.5)] transition-shadow">
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2 text-xs mb-2">
              <Video className="h-4 w-4 text-muted-foreground" />
              Active Works
            </CardDescription>
            <CardTitle className="text-3xl font-bold tabular-nums">
              {metrics.activeWorks}
            </CardTitle>
          </CardHeader>
          <CardFooter className="pt-0">
            <p className="text-xs text-muted-foreground">
              Published works
            </p>
          </CardFooter>
        </Card>

        <Card className="hover:shadow-[0_4px_16px_rgba(0,0,0,0.5)] transition-shadow">
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2 text-xs mb-2">
              <Play className="h-4 w-4 text-muted-foreground" />
              Total Views
            </CardDescription>
            <CardTitle className="text-3xl font-bold tabular-nums">
              {formatViews(metrics.totalViews)}
            </CardTitle>
          </CardHeader>
          <CardFooter className="pt-0">
            <p className="text-xs text-muted-foreground">
              Across all works
            </p>
          </CardFooter>
        </Card>

        <Card className="hover:shadow-[0_4px_16px_rgba(0,0,0,0.5)] transition-shadow">
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2 text-xs mb-2">
              <Target className="h-4 w-4 text-muted-foreground" />
              Campaigns
            </CardDescription>
            <CardTitle className="text-3xl font-bold tabular-nums">
              {metrics.activeCampaigns}
            </CardTitle>
          </CardHeader>
          <CardFooter className="pt-0">
            <p className="text-xs text-muted-foreground">
              {metrics.completedCampaigns} completed
            </p>
          </CardFooter>
        </Card>
      </div>

      {/* Active Campaigns */}
      {activeCampaigns.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className='flex items-center gap-2'>
              <Target className='h-5 w-5' />
              Active Crowdfunding Campaigns
            </CardTitle>
            <CardDescription>
              Track the progress of your ongoing slice fundraising campaigns
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className='space-y-6'>
              {activeCampaigns.map(campaign => {
                const progress =
                  campaign.goal_slices > 0
                    ? ((campaign.total_slices_raised || 0) /
                        campaign.goal_slices) *
                      100
                    : 0;
                const isActive = campaign.status === 'active';

                return (
                  <div
                    key={campaign.id}
                    className='rounded-lg p-5 bg-muted/20 hover:bg-muted/30 transition-colors'
                  >
                    <div className='flex justify-between items-start mb-4'>
                      <div>
                        <h4 className='font-medium'>
                          {(campaign as any).works?.title || 'Untitled Work'}
                        </h4>
                        <p className='text-sm text-muted-foreground'>
                          {(campaign.total_slices_raised || 0).toLocaleString()}{' '}
                          / {campaign.goal_slices.toLocaleString()} slices
                        </p>
                      </div>
                      <Badge
                        variant={isActive ? 'default' : 'secondary'}
                        className={
                          isActive ? 'bg-green-500/10 text-green-500' : ''
                        }
                      >
                        {campaign.status}
                      </Badge>
                    </div>
                    <Progress
                      value={Math.min(progress, 100)}
                      className='mb-2'
                    />
                    <div className='flex justify-between text-sm text-muted-foreground'>
                      <span>{progress.toFixed(1)}% funded</span>
                      <span>
                        {Math.ceil(
                          (new Date(campaign.end_date).getTime() - Date.now()) /
                            (1000 * 60 * 60 * 24)
                        )}{' '}
                        days left
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Works & Activity */}
      <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
        {/* Recent Works */}
        <Card>
          <CardHeader>
            <CardTitle className='flex items-center gap-2'>
              <FileText className='h-5 w-5' />
              Recent Works
            </CardTitle>
          </CardHeader>
          <CardContent>
            {recentWorks.length > 0 ? (
              <div className='space-y-5'>
                {recentWorks.map(work => (
                  <div
                    key={work.id}
                    className='flex items-center justify-between p-4 rounded-lg bg-muted/20 hover:bg-muted/30 transition-colors'
                  >
                    <div className='flex-1'>
                      <p className='font-medium'>{work.title}</p>
                      <div className='flex items-center gap-2 mt-1'>
                        <Badge variant='outline' className='text-xs'>
                          {work.type.replace('_', ' ')}
                        </Badge>
                        <Badge
                          variant={
                            work.status === 'published'
                              ? 'default'
                              : 'secondary'
                          }
                          className={
                            work.status === 'published'
                              ? 'bg-green-500/10 text-green-500 text-xs'
                              : 'text-xs'
                          }
                        >
                          {work.status}
                        </Badge>
                      </div>
                    </div>
                    <div className='text-right text-sm text-muted-foreground'>
                      <p>{(work.view_count || 0).toLocaleString()} views</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className='text-center py-8 text-muted-foreground'>
                <Video className='h-12 w-12 mx-auto mb-3 opacity-50' />
                <p>No works yet</p>
                <p className='text-sm'>Start by creating your first work</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Activity */}
        <Card>
          <CardHeader>
            <CardTitle className='flex items-center gap-2'>
              <Clock className='h-5 w-5' />
              Recent Activity
            </CardTitle>
          </CardHeader>
          <CardContent>
            {recentActivity.length > 0 ? (
              <div className='space-y-5'>
                {recentActivity.map((activity, index) => {
                  const { Icon, color } = activityIcon(activity.type);
                  return (
                    <div key={index} className='flex items-start gap-4 group'>
                      <div
                        className={`p-2.5 rounded-lg bg-muted/30 group-hover:bg-muted/50 ${color} transition-colors`}
                      >
                        <Icon className='h-4 w-4' />
                      </div>
                      <div className='flex-1 min-w-0 pt-0.5'>
                        <p className='text-sm font-medium mb-1'>{activity.title}</p>
                        <p className='text-xs text-muted-foreground mb-1.5'>
                          {activity.description}
                        </p>
                        <p className='text-xs text-muted-foreground/70'>
                          {formatTimeAgo(activity.timestamp)}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className='text-center py-8 text-muted-foreground'>
                <Clock className='h-12 w-12 mx-auto mb-3 opacity-50' />
                <p>No recent activity</p>
                <p className='text-sm'>
                  Activity will appear as viewers interact with your content
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle className='flex items-center gap-2'>
            <Zap className='h-5 w-5' />
            Quick Actions
          </CardTitle>
          <CardDescription>
            Common tasks to manage your Kinora presence
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className='grid grid-cols-2 lg:grid-cols-4 gap-4'>
            <Button
              variant='outline'
              className='h-auto py-6 px-4 flex flex-col items-center gap-3 hover:bg-primary/10 transition-all'
              onClick={() => navigate('/content')}
            >
              <div className='p-2 rounded-lg bg-muted/30'>
                <FileText className='h-5 w-5' />
              </div>
              <span className='text-sm font-medium'>Create Work</span>
            </Button>
            <Button
              variant='outline'
              className='h-auto py-6 px-4 flex flex-col items-center gap-3 hover:bg-primary/10 transition-all'
              onClick={() => navigate('/content')}
            >
              <div className='p-2 rounded-lg bg-muted/30'>
                <Target className='h-5 w-5' />
              </div>
              <span className='text-sm font-medium'>Start Campaign</span>
            </Button>
            <Button
              variant='outline'
              className='h-auto py-6 px-4 flex flex-col items-center gap-3 hover:bg-primary/10 transition-all'
              onClick={() => navigate('/analytics')}
            >
              <div className='p-2 rounded-lg bg-muted/30'>
                <BarChart3 className='h-5 w-5' />
              </div>
              <span className='text-sm font-medium'>View Analytics</span>
            </Button>
            <Button
              variant='outline'
              className='h-auto py-6 px-4 flex flex-col items-center gap-3 hover:bg-primary/10 transition-all'
              onClick={() => navigate('/revenue')}
            >
              <div className='p-2 rounded-lg bg-muted/30'>
                <DollarSign className='h-5 w-5' />
              </div>
              <span className='text-sm font-medium'>Check Revenue</span>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;
